import React, { useCallback, useMemo, useState } from 'react';
import {
	Card,
	TextStyle,
	ChoiceList,
	TextContainer,
	TextField,
	Icon,
	Link,
	Stack,
	Heading,
	Badge,
	Button,
	FormLayout,
	ButtonGroup,
	Spinner,
	Tabs,
} from '@shopify/polaris';
import { DiscountCodeIcon, RefreshIcon } from '@shopify/polaris-icons';
import useAbortController from '../../../Utils/useAbortController';
import { useI18n } from '@shopify/react-i18n';
import { useMountedState, useSessionStorage } from 'react-use';
import PlanBadge from '../../Shared/PlanBadge';

const SubscriptionStatusSection = ({
	shared,
	lastSyncStore,
	products,
	syncingProducts,
	collections,
	pages,
	articles,
}) => {
	const {
		user,
		fetchJson,
		redirectOpt,
		host,
		token,
		plans,
		isSubscribed,
		setIsSubscribed,
		plan,
		setPlan,
		showToast,
	} = shared;
	const abortController = useAbortController();
	const isMounted = useMountedState();
	const [i18n] = useI18n();
	const [isStartingSubscription, setIsStartingSubscription] = useState(false);
	const [isCancellingSubscription, setIsCancellingSubscription] =
		useState(false);

	const [selectedPlan, setSelectedPlan] = useState(plan ?? 'starter');
	const [couponCode, setCouponCode] = useState('');
	const [isCouponCodeInvalid, setIsCouponCodeInvalid] = useState(false);
	const [discountRate, setDiscountRate] = useState(0);

	const startSubscription = useCallback(() => {
		if (user.isDemo) {
			setIsSubscribed(true);
			setPlan(selectedPlan);
			return;
		}

		setIsStartingSubscription(true);

		const payload = { plan: selectedPlan, couponCode, host, token };
		fetchJson(
			'/Subscription/CreateCharge',
			'POST',
			JSON.stringify(payload),
			abortController.signal,
			(data) => {
				if (!isMounted()) return;
				redirectOpt(data.confirmationUrl);
			},
			() => {
				if (!isMounted()) return;
				setIsStartingSubscription(false);
			}
		);

		return () => abortController.abort();
	}, [selectedPlan, couponCode]);

	const cancelSubscription = useCallback(() => {
		if (user.isDemo) {
			setIsSubscribed(false);
			setPlan('');
			setOpen(true);
			return;
		}

		setIsCancellingSubscription(true);

		fetchJson(
			'/Subscription/Cancel',
			'POST',
			'',
			abortController.signal,
			() => {
				if (!isMounted()) return;
				setIsSubscribed(false);
				setPlan('');
				setOpen(true);
			},
			() => {
				if (!isMounted()) return;
				setIsStartingSubscription(false);
			}
		);

		return () => abortController.abort();
	}, []);

	const handlePlanChange = useCallback(
		(value) => setSelectedPlan(value[0]),
		[]
	);

	const handleCouponCodeChange = useCallback((value) => {
		setIsCouponCodeInvalid(false);
		setCouponCode(value.trim().toUpperCase());
	}, []);

	const checkCouponCode = useCallback((couponCode) => {
		if (!couponCode.length) {
			setIsCouponCodeInvalid(false);
			setDiscountRate(0);
			return;
		}

		fetchJson(
			'/Subscription/GetDiscountRate?' +
				new URLSearchParams({
					couponCode,
				}),
			'GET',
			undefined,
			abortController.signal,
			(data) => {
				if (!isMounted()) return;
				setDiscountRate(data);
				setIsCouponCodeInvalid(data == 0);
			}
		);

		return () => abortController.abort();
	}, []);

	const [open, setOpen] = useSessionStorage(
		'html.subscription.open',
		plan == 'free'
	);

	const subscriptionPlanList = (
		<ChoiceList
			title={i18n.translate('SelectSubscriptionPlan')}
			choices={[
				{
					label: i18n.translate('Plan1', {
						price:
							discountRate > 0 ? (
								<>
									<TextStyle variation='negative'>
										<s>
											{i18n.formatCurrency(
												plans.starter *
													(couponCode ==
													plans.annual.couponCode
														? 12
														: 1)
											)}
										</s>{' '}
									</TextStyle>
									<TextStyle variation='positive'>
										{i18n.formatCurrency(
											plans.starter *
												(1 - discountRate) *
												(couponCode ==
												plans.annual.couponCode
													? 12
													: 1)
										)}
									</TextStyle>
								</>
							) : (
								<TextStyle variation='positive'>
									{i18n.formatCurrency(
										plans.starter *
											(couponCode ==
											plans.annual.couponCode
												? 12
												: 1)
									)}
								</TextStyle>
							),
						period: i18n.translate(
							couponCode == plans.annual.couponCode
								? 'Year'
								: 'Month'
						),
					}),
					value: 'starter',
				},
				{
					label: i18n.translate('Plan2', {
						price:
							discountRate > 0 ? (
								<>
									<TextStyle variation='negative'>
										<s>
											{i18n.formatCurrency(
												plans.growth *
													(couponCode ==
													plans.annual.couponCode
														? 12
														: 1)
											)}
										</s>{' '}
									</TextStyle>
									<TextStyle variation='positive'>
										{i18n.formatCurrency(
											plans.growth *
												(1 - discountRate) *
												(couponCode ==
												plans.annual.couponCode
													? 12
													: 1)
										)}
									</TextStyle>
								</>
							) : (
								<TextStyle variation='positive'>
									{i18n.formatCurrency(
										plans.growth *
											(couponCode ==
											plans.annual.couponCode
												? 12
												: 1)
									)}
								</TextStyle>
							),
						period: i18n.translate(
							couponCode == plans.annual.couponCode
								? 'Year'
								: 'Month'
						),
					}),
					value: 'growth',
				},
				{
					label: i18n.translate('Plan3', {
						price:
							discountRate > 0 ? (
								<>
									<TextStyle variation='negative'>
										<s>
											{i18n.formatCurrency(
												plans.pro *
													(couponCode ==
													plans.annual.couponCode
														? 12
														: 1)
											)}
										</s>{' '}
									</TextStyle>
									<TextStyle variation='positive'>
										{i18n.formatCurrency(
											plans.pro *
												(1 - discountRate) *
												(couponCode ==
												plans.annual.couponCode
													? 12
													: 1)
										)}
									</TextStyle>
								</>
							) : (
								<TextStyle variation='positive'>
									{i18n.formatCurrency(
										plans.pro *
											(couponCode ==
											plans.annual.couponCode
												? 12
												: 1)
									)}
								</TextStyle>
							),
						period: i18n.translate(
							couponCode == plans.annual.couponCode
								? 'Year'
								: 'Month'
						),
					}),
					value: 'pro',
				},
			]}
			selected={selectedPlan}
			onChange={handlePlanChange}
		/>
	);

	const couponCodeInput = (
		<TextField
			label={i18n.translate('CouponCode')}
			value={couponCode}
			error={
				isCouponCodeInvalid
					? i18n.translate('CouponCodeErrorMsg', { couponCode })
					: undefined
			}
			prefix={<Icon source={DiscountCodeIcon} />}
			onChange={handleCouponCodeChange}
			onBlur={() => checkCouponCode(couponCode)}
			suffix={i18n.translate('DiscountMsg', {
				discountRate: i18n.formatPercentage(discountRate),
			})}
		/>
	);

	const lastSyncStoreText = lastSyncStore ? (
		<p>
			{i18n.translate('LastSyncMsg', {
				lastSync: (
					<TextStyle variation='positive'>
						{new Date(lastSyncStore).toLocaleString(i18n.locale)}
					</TextStyle>
				),
			})}
		</p>
	) : (
		<p>
			{i18n.translate('NoSyncMsg', {
				neverSynced: (
					<TextStyle variation='warning'>
						{i18n.translate('NeverSynced')}
					</TextStyle>
				),
			})}
		</p>
	);

	const maxProducts = useMemo(() => {
		if (plan == 'starter') return 1000;
		if (plan == 'growth') return 10000;
		if (plan == 'pro') return 100000;
		return 200;
	}, [plan]);

	const resourceStatusText = (
		<Stack alignment='center'>
			<Stack.Item>
				{i18n.translate('Common.Product.plural')}:{' '}
				{syncingProducts ? (
					<TextStyle
						variation={
							syncingProducts >= maxProducts
								? 'negative'
								: syncingProducts >= maxProducts * 0.8
								? 'warning'
								: 'positive'
						}
					>
						{i18n.formatNumber(syncingProducts)}/
						{i18n.formatNumber(maxProducts)}{' '}
						<Spinner size='small' />
					</TextStyle>
				) : (
					<TextStyle
						variation={
							products >= maxProducts
								? 'negative'
								: products >= maxProducts * 0.8
								? 'warning'
								: 'positive'
						}
					>
						{i18n.formatNumber(products)}/
						{i18n.formatNumber(maxProducts)}
					</TextStyle>
				)}
			</Stack.Item>
			<Stack.Item>
				{i18n.translate('Common.Page.plural')}:{' '}
				<TextStyle variation='positive'>
					{i18n.formatNumber(pages)}
				</TextStyle>
			</Stack.Item>
			<Stack.Item>
				{i18n.translate('Common.Collection.plural')}:{' '}
				<TextStyle variation='positive'>
					{i18n.formatNumber(collections)}
				</TextStyle>
			</Stack.Item>
			<Stack.Item>
				{i18n.translate('Common.Article.plural')}:{' '}
				<TextStyle variation='positive'>
					{i18n.formatNumber(articles)}
				</TextStyle>
			</Stack.Item>
		</Stack>
	);

	const [isSyncingStore, setIsSyncingStore] = useState(false);

	const syncStore = useCallback(() => {
		setIsSyncingStore(true);

		fetchJson(
			'/Dashboard/SyncStore',
			'POST',
			undefined,
			abortController.signal,
			() => {
				if (!isMounted()) return;
				showToast({
					content: i18n.translate('SyncStartedMsg'),
				});
			},
			() => {
				if (!isMounted()) return;
				setIsSyncingStore(false);
			}
		);

		return () => abortController.abort();
	}, []);

	const selectedTab = couponCode == plans.annual.couponCode ? 1 : 0;
	const handleTabChange = useCallback((selectedTabIndex) => {
		const newCouponCode =
			selectedTabIndex == 0 ? '' : plans.annual.couponCode;
		setCouponCode(newCouponCode);
		checkCouponCode(newCouponCode);
	}, []);

	const tabs = [
		{
			id: 'monthly-plans',
			content: i18n.translate('MonthlyPlans'),
		},
		{
			id: 'annual-plans',
			content: i18n.translate('AnnualPlans'),
		},
	];

	return (
		<Card
			title={
				<Stack>
					<Heading element='h3'>
						{i18n.translate('Title').toUpperCase()}
					</Heading>
					{isSubscribed ? (
						<PlanBadge plan={plan} />
					) : (
						<Badge status='new'>
							{i18n.translate('Common.Action.Deactivated')}
						</Badge>
					)}
				</Stack>
			}
			actions={[
				isSubscribed && {
					content: i18n.translate('Common.Action.Unsubscribe'),
					destructive: true,
					onAction: cancelSubscription,
					loading: isCancellingSubscription,
				},
				isSubscribed &&
					!open && {
						content: i18n.translate('Action.ChangePlan'),
						onAction: () => setOpen(true),
					},
				!isSubscribed &&
					!open && {
						content: i18n.translate('Common.Action.Subscribe'),
						onAction: () => setOpen(true),
					},
			].filter((action) => action)}
		>
			<Card.Section>
				<TextContainer spacing='loose'>
					<FormLayout>
						{lastSyncStoreText}
						{resourceStatusText}
						<Button
							icon={RefreshIcon}
							onClick={syncStore}
							loading={isSyncingStore}
						>
							{i18n.translate('Action.SyncStore')}
						</Button>
					</FormLayout>
					{open && (
						<Tabs
							tabs={tabs}
							selected={selectedTab}
							onSelect={handleTabChange}
						>
							<Card.Section>
								<FormLayout>
									{subscriptionPlanList}
									{couponCode != plans.annual.couponCode &&
										couponCodeInput}
									<ButtonGroup>
										<Button
											primary
											loading={isStartingSubscription}
											onClick={startSubscription}
										>
											{i18n.translate(
												'Common.Action.Subscribe'
											)}
										</Button>
										<Button
											plain
											onClick={() => setOpen(false)}
										>
											{i18n.translate('RemindMeLater')}
										</Button>
									</ButtonGroup>
								</FormLayout>
							</Card.Section>
						</Tabs>
					)}
				</TextContainer>
			</Card.Section>
		</Card>
	);
};

export default SubscriptionStatusSection;
